import { Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { messages } from '../../../constants/messages';
import { ToastrHelper } from '../../../helpers/toastr.helper';
import { UpdateCompleteProfileCommand } from '../../../models/identity/update-complete-profile-command.model';
import { UserStatusTypes } from '../../../models/identity/user-status-types.model';
import { User } from '../../../models/identity/user.model';
import { CropperParams } from '../../../models/shared/cropper-params.model';
import { AccountsService } from '../../../services/identity/accounts.service';
import { ProfileService } from '../../../services/identity/profile.service';
import { ImageService } from '../../../services/shared/image.service';
import { ImageCropModalComponent } from '../../../shared/components/image-crop-modal/image-crop-modal.component';

@Component({
    selector: 'app-onboarding-user-completion',
    templateUrl: './onboarding-user-completion.component.html',
    styleUrls: ['./onboarding-user-completion.component.scss'],
})
export class OnboardingUserCompletionComponent {
    @Output() public completed = new EventEmitter<boolean>();
    public cropperParams: CropperParams = new CropperParams();
    public form: FormGroup;
    // Child Components
    @ViewChild(ImageCropModalComponent) public imageCropModal: ImageCropModalComponent;
    public isLoading = false;
    public profilePicture: string;

    constructor(
        public readonly imageService: ImageService,
        private readonly toastrHelper: ToastrHelper,
        private readonly formBuilder: FormBuilder,
        private readonly profileService: ProfileService,
        private readonly accountsService: AccountsService,
        private readonly translate: TranslateService,
    ) {
        this.form = this.formBuilder.group({
            description: ['', [Validators.maxLength(2600)]],
        });
    }

    public get hasProfilePicture(): boolean {
        return !!this.accountsService.currentUser.profilePicture;
    }

    public removePicture() {
        this.profilePicture = '';
        this.accountsService.currentUser.profilePicture = this.profilePicture;
    }

    public async save() {
        if (this.isLoading === true) {
            return;
        }

        this.isLoading = true;
        const command: UpdateCompleteProfileCommand = new UpdateCompleteProfileCommand(this.accountsService.currentUser);

        command.description = this.form.controls.description?.value ?? '';
        command.isOnBoardingProfileCompleted = true;
        command.status = UserStatusTypes.Active;

        if (this.profilePicture !== '') {
            command.profilePicture = this.profilePicture;
            command.pictureHasChanged = true;
        }

        this.profileService.updateCompleteProfile(command).subscribe(
            (updatedUser: User) => {
                this.toastrHelper.showSuccess(this.translate.instant('toastMessages.apiDataPersistenceSuccess'));

                this.accountsService.currentUser.profilePicture = updatedUser.profilePicture;
                this.accountsService.currentUser.pictureReference = updatedUser.pictureReference;
                this.accountsService.currentUser.description = updatedUser.description;
                this.accountsService.currentUser.isOnBoardingProfileCompleted = updatedUser.isOnBoardingProfileCompleted;
                this.accountsService.currentUser.active = updatedUser.active;
                this.accountsService.currentUser.status = updatedUser.status;

                this.accountsService.saveCurrentUser();
                this.accountsService.refreshCurrentUser();

                this.isLoading = false;
                this.completed.emit(true);
            },
            (error) => {
                this.toastrHelper.showGenericError(error);
                this.isLoading = false;
                this.completed.emit(false);
            },
        );
    }

    public savePicture() {
        const format = this.imageService.getFormatFromBase64(this.imageService.croppedImage as string);

        if (this.imageService.isInBadFormat(format)) {
            this.toastrHelper.showError(messages.pictureExtensionError);
            return;
        }

        if (this.imageService.isExceedingPictureMaxSize(this.imageService.getImageSizeFromBase64(this.imageService.croppedImage as string))) {
            this.toastrHelper.showError(messages.profilePictureSizeError + this.imageService.getMaxSizeInString());
            return;
        }

        this.profilePicture = this.imageService.croppedImage as string;
        this.accountsService.currentUser.profilePicture = this.profilePicture;

        this.imageService.initCropperValues();
    }
}
